import {
    ApolloClient,
    ApolloLink,
    defaultDataIdFromObject,
    HttpLink,
    InMemoryCache,
    type NormalizedCacheObject,
    type Operation,
    type ServerError,
    type ServerParseError,
    type TypePolicies,
} from '@apollo/client';
import {type ErrorHandler, onError as createErrorLink} from '@apollo/client/link/error';
import {RestLink} from 'apollo-link-rest';
import {GraphQLError} from 'graphql';
import {Map} from 'immutable';
import fetch from 'node-fetch';

import {REST_GRAPHQL_CLIENTNAME} from '@famly/stat_apollo-client';
import type apiHelper from 'web-app/services/api-helper';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {fetchVacationTypePatcher} from 'web-app/api/clients/rest-types/fetch-vacation/fetch-vacation-type-patcher';
import {holidayRsvpTypePatcher} from 'web-app/api/clients/rest-types/holiday-rsvp/holiday-rsvp-type-patcher';

import {checkinPincodesTypePatcher} from './rest-types/checkin-pincodes/checkin-pincodes-type-patcher';
import {Headers as Constants} from './constants';
import graphqlTypesToMergeInCache from './graphql-types-to-merge-in-apollo-cache.json';
import {addChildLeaveTypePatcher} from './rest-types/add-child-leave/add-child-leave-type-patcher';
import {childLeaveSummaryTypePatcher} from './rest-types/child-leave-summary/child-leave-summary-type-patcher';
import {downloadAttendanceReportTypePatcher} from './rest-types/download-attendance-report/download-attendance-report-type-patcher';
import {editPaymentPatcher} from './rest-types/edit-payment/edit-payment-patcher';
import {downloadBookingsOrgTypePatcher} from './rest-types/download-bookings-org/download-bookings-org-type-patcher';
import {employeeLeaveSummaryWithIndependentPeriodsTypePatcher} from './rest-types/employee-leave-summary/employee-leave-summary-with-independent-periods-type-patcher';
import {addPaymentPatcher} from './rest-types/add-payment/add-payment-patcher';
import {openingHoursTypePatcher} from './rest-types/opening-hours/opening-hours-type-patcher';
import {persistFileTypePatcher} from './rest-types/persist-file/persist-file-type-patcher';
import {uploadRequestTypePatcher} from './rest-types/upload-request/upload-request-type-patcher';
import {conversationsTypePatcher} from './rest-types/conversations/conversations-type-patcher';
import {postSendersOptionsTypePatcher} from './rest-types/post-senders-options/post-senders-options-type-patcher';
import {parentalPermissionsTypePatcher} from './rest-types/parental-permissions/parental-permissions-type-patcher';
import {parentDocumentTypePatcher} from './rest-types/parent-document/parent-document-type-patcher';
import {parentDocumentForChildProfileTypePatcher} from './rest-types/parent-document-for-child-profile/parent-document-for-child-profile-type-patcher';
import {parentDocumentAcknowledgementStatusTypePatcher} from './rest-types/parent-document-acknowledgement-status/parent-document-acknowledgement-status-type-patcher';
import {fetchChildrenTypePatcher} from './rest-types/fetch-children/fetch-children-type-patcher';
import {childSummaryTypePatcher} from './rest-types/child-summary/child-summary-type-patcher';
import {healthDetailsTypePatcher} from './rest-types/health-details/health-details-type-patcher';
import {deletePaymentPatcher} from './rest-types/edit-payment-delete/delete-payment-patcher';
import {tagsTypePatcher} from './rest-types/tags/tags-type-patcher';
import {permissionsTypePatcher} from './rest-types/permissions/permissions-type-patcher';
import {createVoucherCompanyPatcher} from './rest-types/create-voucher-company/create-voucher-company-patcher';
import {createBillingPlanPatcher} from './rest-types/create-billing-plan/create-billing-plan-type-patcher';
import {paymentOptionsTypePatcher} from './rest-types/payment-options/payment-options-type-patcher';
import {paymentMethodPatcher} from './rest-types/payment-method/payment-method-patcher';
import {invoicePatcher} from './rest-types/invoice/invoice-patcher';
import {voucherCompanyPatcher} from './rest-types/voucher-company/voucher-company-patcher';
import {productsTypePatcher} from './rest-types/products/products-type-patcher';
import {sessionsTypePatcher} from './rest-types/sessions/sessions-type-patcher';
import {billPayersTypePatcher} from './rest-types/bill-payers/bill-payers-type-patcher';
import {updateBillPayerNotesTypePatcher} from './rest-types/bill-payer-notes/bill-payer-notes-type-patcher';
import {changeEmailPatcher} from './rest-types/change-email/change-email-patcher';
import {createProductPatcher} from './rest-types/create-product/create-product-type-patcher';
import {createSessionPackagePatcher} from './rest-types/create-session-package/create-session-package-type-patcher';
import {newGroupPatcher} from './rest-types/new-group/new-group-patcher';
import {addClosureDayPatcher} from './rest-types/add-closure-day/add-closure-day-type-patcher';
import {fetchClosureDayPatcher} from './rest-types/fetch-closure-day/fetch-closure-day-type-patcher';
import {discountsTypePatcher} from './rest-types/discounts/discounts-type-patcher';
import {addInstitutionToAreaPatcher} from './rest-types/add-institution-to-area/add-institution-to-area-patcher';
import {removeInstitutionFromAreaPatcher} from './rest-types/remove-institution-from-area/remove-institution-from-area-patcher';
import {confirmPaymentPatcher} from './rest-types/confirm-payment/confirm-payment-patcher';
import {ConfirmPaymentDeletePatcher} from './rest-types/confirm-payment-delete/confirm-payment-delete-patcher';
import {parentPermissionRecipientTypePatcher} from './rest-types/parent-permission-recipient/parent-permission-recipient-type-patcher';
import {newDepartmentPatcher} from './rest-types/new-department/new-department-type-patcher';
import {fetchPlanWarningsTypePatcher} from './rest-types/fetch-plan-warnings-for-child/fetch-plan-warnings-for-child-type-patcher';
import {validateBillingPlanPatcher} from './rest-types/validate-billing-plan/validate-billing-plan-type-patcher';
import {paymentPatcher} from './rest-types/payments/payments-patcher';
import {balancePatcher} from './rest-types/balance/balance-patcher';
import {parentDocumentCanCreateTypePatcher} from './rest-types/parent-document-can-create/parent-document-can-create-type-patcher';
import {debtOverviewPatcher} from './rest-types/debt-overview/debt-overview-patcher';

const authLink = (APIHelper: typeof apiHelper) =>
    new ApolloLink((operation, forward) => {
        const context = operation.getContext();
        const originalHeaders = context.headers || {};

        const {headers} = APIHelper.getRequestHeaders(!context.skipAuthentication, {
            operationName: operation.operationName,
        });

        operation.setContext({
            headers: {
                ...originalHeaders,
                ...headers,
            },
        });

        return forward(operation);
    });

type ErrorResponse = Parameters<ErrorHandler>[0];
export type NetworkError = ErrorResponse['networkError'] | GraphQLError;
type NetworkExtra = {
    name: string;
    requestUUID: string;
    responseError?: GraphQLError[];
    errorCode?: number;
    log: boolean;
};

type ErrorCallbackParams<ErrorType, Type extends string> = [
    ErrorType,
    NetworkExtra | null,
    string | null,
    ReturnType<Operation['getContext']>,
    Type,
];

/**
 * Overloaded function signature telling implementers that the error callback will either be called with Network errors
 * or GraphQL errors, and should use the last parameter to discriminate.
 */
export type OnErrorCallback = (
    ...args:
        | ErrorCallbackParams<ErrorResponse['networkError'], 'network-error'>
        | ErrorCallbackParams<GraphQLError, 'graphql-error'>
) => void;

export const isServerError = (error: NetworkError): error is ServerError =>
    hasValue(error) && error.name === 'ServerError';
export const isServerParseError = (error: NetworkError): error is ServerParseError =>
    hasValue(error) && error.name === 'ServerParseError';

/**
 * Represents the errors the `core` PHP project returns
 */
type RestError = {
    error: string;
    errorCode: number;
    log: boolean;
};

const isRestError = (error: NetworkError): error is NetworkError & {result: RestError} => {
    const result = (error as any)?.result;
    return hasValue(result) && hasValue(result.error) && hasValue(result.errorCode) && hasValue(result.log);
};

const errorLink = (onError: OnErrorCallback) =>
    createErrorLink(({graphQLErrors, networkError, operation, response}) => {
        const context = operation.getContext();
        const rawResponse = context.response;
        const requestUUID = rawResponse && rawResponse.headers.get(Constants.XFamlyRequestUuid);
        const name = operation.operationName;
        const responseErrors = response && response.errors ? response.errors : null;

        const extra = {
            name,
            requestUUID,
            responseErrors,
        };

        if (graphQLErrors) {
            // No reason to log the same error multiple times per request
            const deDuplicatedErrors = Map(
                graphQLErrors.map(error => {
                    const key = error.message + (error.extensions ? error.extensions.errorCode : '');
                    return [key, error];
                }),
            )
                .valueSeq()
                .toArray();

            deDuplicatedErrors.map(error => {
                onError(
                    error,
                    {
                        ...extra,
                        log:
                            hasValue(error.extensions) && hasValue(error.extensions.log)
                                ? (error.extensions.log as boolean)
                                : true,
                    },
                    null,
                    context,
                    'graphql-error',
                );
            });
        }

        if (context?.clientName === 'restGraphQl' && isRestError(networkError)) {
            // Apollo doesn't have any way of handling errors returned by `core` when using RestLink.
            // If we don't do anything, we'll end up showing the user a generic error message like:
            // "Response not successful: Received status code 400"
            // To avoid this, we "cheat" and construct a new error, and pass it along as if it were
            // a regular GraphQL error.
            const graphQLError = new GraphQLError(networkError.result.error, {
                extensions: {
                    errorCode: networkError.result.errorCode,
                    log: networkError.result.log,
                    display: 'Transient',
                    severity: 'Error',
                },
            });

            onError(
                graphQLError,
                {
                    ...extra,
                    log: networkError.result.log,
                },
                null,
                context,
                'graphql-error',
            );
        } else if (networkError) {
            const networkExtra =
                isServerError(networkError) && networkError.result.errorCode
                    ? {
                          ...networkError.result,
                          ...extra,
                          log:
                              networkError.result && hasValue(networkError.result.log) ? networkError.result.log : true,
                      }
                    : null;

            const hasOnlyNetworkError = !hasValue(graphQLErrors) || graphQLErrors.length === 0;

            // Only report network errors to Sentry - don't show them in the UI
            onError(
                hasOnlyNetworkError ? networkError : undefined,
                networkExtra,
                hasOnlyNetworkError && 'result' in networkError && networkError?.result
                    ? networkError.result.message
                    : null,
                context,
                'network-error',
            );
        }
    });

let persistedClients = Map<string, ApolloClient<NormalizedCacheObject>>();

// There's a type mismatch between `@types/node-fetch` and `HttpLink.Options['fetch']`
// Since we're only using `node-fetch` for react-testing-library we'll just cast the type.
const typedFetch = fetch as unknown as WindowOrWorkerGlobalScope['fetch'];

/**
 * Creates an http link for the given url.
 *
 * If this is run in a test environment, then
 *
 * 1) the operation name of the graphql operation will be appended to the url as query param
 * 2) a fetcher function is provided as this is needed for the react testing library
 *
 * @param url the url of the graphql endpoint
 * @returns an HttpLink with the said properties
 */
function createHttpLink(url: string): HttpLink {
    const isTestingEnvironment = process.env.TEST_TYPE === 'jest';
    return new HttpLink({
        uri: operation => {
            /**
             * This adds a NOOP query string to the graphql URL which solely consists of the
             * operation name. This is exclusively done to improve the development experience
             */
            return `${url}?${withOperationName(operation)}`;
        },
        // A fetcher function is required when React testing library tests are run, if the fetcher function is not found then that will result into a failing test.
        fetch: isTestingEnvironment ? typedFetch : undefined,
    });
}

export const withOperationName = (operation: Operation) => {
    const {operationName} = operation;
    try {
        // Generic way for queries to add hints to the operation name
        const {affix} = operation.getContext();

        if (hasValue(affix)) {
            return `${operationName}_${affix}`;
        }
    } catch {
        return operationName;
    }
    return operationName;
};

/**
 * Creates a apollo rest link for the given url.
 *
 * Routes the rest calls handled by graphql apollo to the given url. Also, defines the
 * types and type patcher (adapter) for all defined graphql rest calls.
 *
 * @param uri the uri of the rest endpoints
 * @returns an RestLink with the said properties
 */
function createRestLink(uri: string): RestLink {
    const isTestingEnvironment = process.env.TEST_TYPE === 'jest';
    return new RestLink({
        uri,
        // A fetcher function is required when React testing library tests are run, if the fetcher function is not found then that will result into a failing test.
        customFetch: isTestingEnvironment ? typedFetch : undefined,
        typePatcher: {
            AddPayment: addPaymentPatcher,
            Balance: balancePatcher,
            ChildLeaveSummary: childLeaveSummaryTypePatcher,
            PersistFile: persistFileTypePatcher,
            ConfirmPayment: confirmPaymentPatcher,
            ConfirmPaymentDelete: ConfirmPaymentDeletePatcher,
            EditPayment: editPaymentPatcher,
            AddChildLeave: addChildLeaveTypePatcher,
            CheckinPincodes: checkinPincodesTypePatcher,
            NewGroup: newGroupPatcher,
            PaymentOptions: paymentOptionsTypePatcher,
            CreateVoucherCompany: createVoucherCompanyPatcher,
            EmployeeLeaveSummaryWithIndependentPeriods: employeeLeaveSummaryWithIndependentPeriodsTypePatcher,
            RemoveInstitutionFromArea: removeInstitutionFromAreaPatcher,
            OpeningHours: openingHoursTypePatcher,
            UploadRequest: uploadRequestTypePatcher,
            DownloadChildAttendanceReport: downloadAttendanceReportTypePatcher,
            DownloadBookingsOrg: downloadBookingsOrgTypePatcher,
            Conversations: conversationsTypePatcher,
            AddClosureDay: addClosureDayPatcher,
            FetchClosureDay: fetchClosureDayPatcher,
            FetchVacation: fetchVacationTypePatcher,
            DeletePayment: deletePaymentPatcher,
            HolidayRsvp: holidayRsvpTypePatcher,
            PaymentMethod: paymentMethodPatcher,
            Payment: paymentPatcher,
            FetchInvoices: invoicePatcher,
            NewDepartment: newDepartmentPatcher,
            PostSendersOptions: postSendersOptionsTypePatcher,
            ParentalPermissions: parentalPermissionsTypePatcher,
            ParentDocument: parentDocumentTypePatcher,
            ParentDocumentForChildProfile: parentDocumentForChildProfileTypePatcher,
            ParentDocumentAcknowledgementStatusResult: parentDocumentAcknowledgementStatusTypePatcher,
            FetchChildren: fetchChildrenTypePatcher,
            ChildSummary: childSummaryTypePatcher,
            VoucherCompanyOptions: voucherCompanyPatcher,
            AddInstitutionToArea: addInstitutionToAreaPatcher,
            HealthDetails: healthDetailsTypePatcher,
            Tags: tagsTypePatcher,
            Permissions: permissionsTypePatcher,
            Sessions: sessionsTypePatcher,
            CreateBillingPlan: createBillingPlanPatcher,
            Products: productsTypePatcher,
            ChangeEmail: changeEmailPatcher,
            BillPayers: billPayersTypePatcher,
            UpdateBillPayerNotes: updateBillPayerNotesTypePatcher,
            CreateProduct: createProductPatcher,
            CreateSessionPackage: createSessionPackagePatcher,
            Discounts: discountsTypePatcher,
            ParentPermissionRecipient: parentPermissionRecipientTypePatcher,
            FetchPlanWarnings: fetchPlanWarningsTypePatcher,
            ValidateBillingPlan: validateBillingPlanPatcher,
            ParentDocumentCanCreate: parentDocumentCanCreateTypePatcher,
            DebtOverview: debtOverviewPatcher,
        },
    });
}

const typePolicies: TypePolicies = Object.fromEntries(graphqlTypesToMergeInCache.map(name => [name, {merge: true}]));
const typePoliciesWithMergeStrategies: TypePolicies = {
    ...typePolicies,

    // since the apollo 3 update, we need to define unique identifiers on all fields
    // those can be combined identifiers, like database keys, too.
    // wherever those are missing so far and neither id, _id nor key are present,
    // the cache updates will fail.
    Child: {
        fields: {
            groups: {
                keyArgs: ['groupId', 'date'],
                merge: (_, incoming) => incoming,
            },
            states: {
                keyArgs: ['from', 'to'],
                merge: (_, incoming) => incoming,
            },
        },
    },
};

export default (
    APIHelper: typeof apiHelper,
    onError: OnErrorCallback,
    overridingApiKey: string | undefined,
    middleWare: ApolloLink,
) => {
    const apiKey = overridingApiKey || APIHelper.apiKey;

    let persistedClient = persistedClients.get(apiKey);

    if (!persistedClient) {
        const httpLink = createHttpLink(APIHelper.getGraphQLUrlByKey(apiKey));
        const restHttpLink = createRestLink(APIHelper.getApiUrlByKey(apiKey));

        const commonHandler = [middleWare, errorLink(onError), authLink(APIHelper)];
        const link = ApolloLink.from(commonHandler.concat(httpLink).filter(hasValue));
        const restLink = ApolloLink.from(commonHandler.concat(restHttpLink).filter(hasValue));

        persistedClient = new ApolloClient({
            // Kudos: https://stackoverflow.com/questions/69629051/using-multiple-endpoints-in-apollo-client
            link: ApolloLink.split(
                operation => {
                    return operation.getContext().clientName === REST_GRAPHQL_CLIENTNAME;
                },
                restLink,
                link,
            ),
            cache: new InMemoryCache({
                possibleTypes: {
                    SynchronizationStatus: ['Synced', 'SyncError', 'Syncing', 'NotSynced'],
                    HeadcountInterface: ['HeadcountTag', 'HeadcountGroup'],
                    Vaccination: ['VaccinationGiven', 'VaccinationExempted'],
                    InvoicePDF: ['InvoicePDFBase64', 'InvoicePDFFile'],
                    PaymentResponse: ['PaymentResponseCompleted', 'PaymentResponseRequiresAction'],
                    PaymentsAccount: ['StripeAccount'],
                    SubscriptionPackagePricing: [
                        'SubscriptionPackagePricingAnnual',
                        'SubscriptionPackagePricingFree',
                        'SubscriptionPackagePricingMonthly',
                    ],
                    RoleAssignee: ['EmployeeAssignee', 'OrganizationAssignee'],
                    PermissionTree: ['PermissionLeaf', 'PermissionNode'],
                    QualificationAssignment: ['Badge', 'Level'],
                    Funding: ['FlatRate', 'PerItem'],
                    Report: ['BuiltInReport', 'QrveyReport'],

                    /**
                     * Fragment matchers for the Category Onboarding feature in Famly Home.
                     *
                     * Ensure that you add entries here when adding new categories, sections and questions.
                     */
                    CategoryOnboardingV4Question: [
                        'CategoryOnboardingV4NurseryInfoQuestion',
                        'CategoryOnboardingV4NurseryProfileQuestion',
                        'CategoryOnboardingV4NurseryLogoQuestion',
                        'CategoryOnboardingV4PinCodeQuestion',
                        'CategoryOnboardingV4ProfileInfoQuestion',
                        'CategoryOnboardingV4OpeningHoursQuestion',
                        'CategoryOnboardingV4ClosingDaysQuestion',
                        'CategoryOnboardingV4ChildrenQuestion',
                        'CategoryOnboardingV4AgeGroupsQuestion',
                        'CategoryOnboardingV4RoomsQuestion',
                        'CategoryOnboardingV4RolesQuestion',
                        'CategoryOnboardingV4StaffQuestion',
                        'CategoryOnboardingV4WhoShouldSignChildrenInAndOutQuestion',
                        'CategoryOnboardingV4ShouldParentsAndStaffUseTheSignInKioskQuestion',
                        'CategoryOnboardingV4HowToSignChildrenAndStaffInQuestion',
                        'CategoryOnboardingV4HowToSignChildrenAndStaffInWithPinQuestion',
                        'CategoryOnboardingV4YourQRCodeIsReadyQuestion',
                        'CategoryOnboardingV4SetupAddressForLocationBasedSignInQuestion',
                        'CategoryOnboardingV4WhatDeviceForYourSignInKioskQuestion',
                        'CategoryOnboardingV4InstructionsForUsingSignInKioskQuestion',
                        'CategoryOnboardingV4CountryQuestion',
                        'CategoryOnboardingV4LearningFrameworksForEnglandQuestion',
                        'CategoryOnboardingV4ReferenceMaterialsForEnglandQuestion',
                        'CategoryOnboardingV4AssessmentsQuestion',
                        'CategoryOnboardingV4StatementBasedTrackingQuestion',
                        'CategoryOnboardingV4LearningFrameworksForScotlandQuestion',
                        'CategoryOnboardingV4ReferenceMaterialsForScotlandQuestion',
                        'CategoryOnboardingV4CanParentsWriteObservationsQuestion',
                        'CategoryOnboardingV4LearningFrameworksForWalesQuestion',
                        'CategoryOnboardingV4ReferenceMaterialsForWalesQuestion',
                        'CategoryOnboardingV4DesignYourOwnAssessmentConfigurationQuestion',
                        'CategoryOnboardingV4BuildYourOwnCurriculumQuestion',
                        'CategoryOnboardingV4SetUpInvoicingBasicsQuestion',
                        'CategoryOnboardingV4InvoiceDeadlineQuestion',
                        'CategoryOnboardingV4InvoiceSendScheduleQuestion',
                        'CategoryOnboardingV4InvoiceSendPeriodQuestion',
                        'CategoryOnboardingV4AssignNewBillPayersQuestion',
                        'CategoryOnboardingV4InvoiceLayoutQuestion',
                        'CategoryOnboardingV4AddPlansToChildrenQuestion',
                        'CategoryOnboardingV4AllowUsersToTranslateMessageAndPostsQuestion',
                        'CategoryOnboardingV4EnableSidekickQuestion',
                        'CategoryOnboardingV4WhoShouldSeeSeenByReceiptsQuestion',
                        'CategoryOnboardingV4HowShouldParentsInfoBeDisplayedOnNewsfeedQuestion',
                        'CategoryOnboardingV4ShouldParentsBeAbleToSendMessagesToOtherParentsQuestion',
                        'CategoryOnboardingV4CanParentsDownloadNewsfeedPhotosFromTheAppQuestion',
                        'CategoryOnboardingV4WhenShouldYourStaffBeAutomaticallyLoggedOutQuestion',
                    ],
                    CategoryOnboardingV4Category: [
                        'CategoryOnboardingV4BasicsCategory',
                        'CategoryOnboardingV4AttendanceCategory',
                        'CategoryOnboardingV4ChildDevelopmentCategory',
                        'CategoryOnboardingV4FinanceCategory',
                        'CategoryOnboardingV4CommunicationCategory',
                    ],
                    CategoryOnboardingV4SectionState: [
                        'CategoryOnboardingV4SectionStateCompleted',
                        'CategoryOnboardingV4SectionStateInProgress',
                        'CategoryOnboardingV4SectionStateNotStarted',
                    ],
                    CategoryOnboardingV4Section: [
                        'CategoryOnboardingV4YourProfileSection',
                        'CategoryOnboardingV4YourNurserySection',
                        'CategoryOnboardingV4RoomsSection',
                        'CategoryOnboardingV4ChildrenSection',
                        'CategoryOnboardingV4StaffSection',
                        'CategoryOnboardingV4SignInOptionsSection',
                        'CategoryOnboardingV4SignInKioskSection',
                        'CategoryOnboardingV4LearningBasicsSection',
                        'CategoryOnboardingV4CurriculumBuilderSection',
                        'CategoryOnboardingV4InvoicingBasicsSection',
                        'CategoryOnboardingV4AutomaticInvoicingSection',
                        'CategoryOnboardingV4InvoiceLayoutSection',
                        'CategoryOnboardingV4ChildPlansSection',
                        'CategoryOnboardingV4WritingAssistanceSection',
                        'CategoryOnboardingV4PrivacySection',
                    ],
                    SignupV8Question: [
                        'SignupV8QuestionsChooseYourPassword',
                        'SignupV8QuestionsWhatsYourRole',
                        'SignupV8QuestionsWhatIsYourLicensedCapacity',
                        'SignupV8QuestionsHowManySitesInGroup',
                        'SignupV8QuestionsWhatPreviouslyUsedChildCareSystems',
                        'SignupV8QuestionsWhatAreYourExpectations',
                        'SignupV8QuestionsWhatTypeOfSite',
                        'SignupV8QuestionsTestChild',
                    ],
                    // Video: ['TranscodingVideo', 'TranscodedVideo'],
                    // Commented out since Apollo figures this out by itself,
                    // EDIT: We still need to do this for interfaces, but apollo will automagically figure it out with unions
                },
                typePolicies: typePoliciesWithMergeStrategies,
                dataIdFromObject: object => {
                    const fallbackId = defaultDataIdFromObject(object);
                    switch (object.__typename) {
                        // We use deviceId instead of id for SignInDevices, which we probably should not.
                        case 'SignInDevice': {
                            const objectCandidate = object as typeof object & {deviceId?: string};
                            return `SignInDevice:${objectCandidate?.deviceId}`;
                        }
                        case 'BillPayerWithBalance': {
                            const objectCandidate = object as typeof object & {billPayer?: {billPayerId?: string}};
                            const id = objectCandidate?.billPayer?.billPayerId;

                            if (!hasValue(id)) {
                                return fallbackId;
                            }

                            return `BillPayerWithBalance:${id}`;
                        }
                        case 'SubmittedFormFieldData':
                            // ids are referring to another id in another structure.
                            // this means ids here are used in an n->1 relation, thus can be duplicated
                            // in order to not have issues in the cache, we need to make them unique again
                            return `${object.id}:${(object as typeof object & {value: string}).value}`;
                        default:
                            return fallbackId;
                    }
                },
            }),
        });

        persistedClients = persistedClients.set(apiKey, persistedClient);
    }

    return persistedClient;
};
